
/* eslint-disable no-magic-numbers */

/* eslint-disable @typescript-eslint/class-methods-use-this -- for now*/
import type { CID } from "multiformats/cid";
import { createHelia } from "helia";
import { unixfs } from '@helia/unixfs';
import { IDBBlockstore } from 'blockstore-idb';
import { IDBDatastore } from 'datastore-idb';
import { MemoryBlockstore } from "blockstore-core";
import { bitswap } from '@helia/block-brokers'
import { fixedSize } from 'ipfs-unixfs-importer/chunker';

import { P2P_CONFIG, CHUNK_SIZE } from '@/config/p2pConfig';
import type {
  HeliaNode,
  ProgressCallback,
  AddFileResult,
  FileMetadata,
  HeliaNodeOptions
} from "@/types/poc/helia";
import type { LibP2pWithServices } from "@/types/poc/libp2p";
import type { LoggerService } from "@/services/abstract/LoggerService";
import type { Logger } from "@/types/Logger";
import { HeliaService } from "@/services/abstract/poc/helia.service.interface";

import { fileToAsyncIterable, concatUint8Arrays } from "./dataUtils";
import { parseCid, filenameFromCid } from "./cidUtils";

// Constants for storage
const BLOCKSTORE_NAME = 'helia-blockstore';
const DATASTORE_NAME = 'helia-datastore';

/**
 * Improved HeliaService that incorporates core logic and manages state
 * Eliminates thin wrapper anti-pattern and ESLint violations
 */
/** Stores references to IDB stores for proper cleanup */
interface IDBStoreRefs {
  blockstore: IDBBlockstore;
  datastore: IDBDatastore;
}

export class HeliaServiceImpl extends HeliaService {
  private readonly log: Logger;
  // State management for node caching and configuration
  private readonly nodeCache = new Map<string, HeliaNode>();
  private readonly storeCache = new Map<string, IDBStoreRefs>();
  private readonly defaultOptions: HeliaNodeOptions;

  constructor(loggerService: LoggerService, defaultOptions: HeliaNodeOptions = {}) {
    super();
    this.log = loggerService.createLogger({ namespace: 'six3ro:p2p:helia' });
    this.defaultOptions = defaultOptions;
  }

  // ============= NODE LIFECYCLE MANAGEMENT =============

  public async createNode(
    libp2p: LibP2pWithServices,
    options?: HeliaNodeOptions
  ): Promise<HeliaNode> {
    const nodeOptions = { ...this.defaultOptions, ...options };
    const cacheKey = this.generateNodeCacheKey(libp2p, nodeOptions);

    // Check cache first
    const cached = this.nodeCache.get(cacheKey);
    if (cached != null) {
      return cached;
    }

    // Create new node - default to persistent storage (IndexedDB)
    const node = (nodeOptions.useMemory ?? false)
      ? await this.createMemoryNode(libp2p)
      : await this.createPersistentNode(libp2p, nodeOptions, cacheKey);

    this.log.debug('Registered protocols:', node.helia.libp2p.getProtocols());

    // Cache the node
    this.nodeCache.set(cacheKey, node);
    return node;
  }

  public async destroyNode(node: HeliaNode): Promise<void> {
    try {
      // Stop the Helia node first
      await node.helia.stop();

      // Find and remove from cache, also close IDB stores
      for (const [key, cachedNode] of this.nodeCache.entries()) {
        if (cachedNode === node) {
          this.nodeCache.delete(key);

          // Close IDB stores if they exist for this node
          const stores = this.storeCache.get(key);
          if (stores != null) {
            this.log.debug(`   🗄️ Closing IDB stores for node: ${key}`);
            await Promise.all([
              stores.blockstore.close(),
              stores.datastore.close()
            ]);
            this.storeCache.delete(key);
            this.log.debug(`   ✅ IDB stores closed`);
          }
          break;
        }
      }
    } catch (error) {
      throw new Error(`Failed to destroy Helia node: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ============= FILE OPERATIONS =============

  public async addFile(
    node: HeliaNode,
    file: File,
    onProgress?: ProgressCallback
  ): Promise<AddFileResult> {
    const startTime = Date.now();
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    this.log.debug(`Upload started: ${file.name} (${fileSizeMB}MB)`);

    this.validateFile(file);

    let blocksStored = 0;
    let bytesStored = 0;

    // Use streaming instead of loading entire file into memory
    const fileIterator = this.streamToIterator(file.stream());

    const cid = await node.fs.addByteStream(fileIterator, {
      chunker: fixedSize({ chunkSize: CHUNK_SIZE }),
      onProgress: (evt) => {
        if (evt.type === 'unixfs:importer:progress:file:write') {
          blocksStored++;
          const written = Number(evt.detail.bytesWritten);
          bytesStored += written;

          if (blocksStored % 50 === 0) {
            const elapsed = (Date.now() - startTime) / 1000;
            const mb = (bytesStored / 1024 / 1024).toFixed(2);
            this.log.debug('Import progress', { mb, blocks: blocksStored, elapsedSeconds: elapsed });
          }

          if (onProgress != null) {
            onProgress(bytesStored, file.size);
          }
        }
      }
    });

    const elapsed = ((Date.now() - startTime) / 1000).toFixed(2);
    this.log.debug(`Upload complete: ${file.name} (CID: ${cid.toString()}, ${elapsed}s)`);

    return {
      cid,
      path: file.name,
      size: file.size
    };
  }

  public async getFile(
    node: HeliaNode,
    cid: string | CID,
    onProgress?: ProgressCallback
  ): Promise<Uint8Array> {
    try {
      const parsedCid = typeof cid === 'string' ? parseCid(cid) : cid;
      this.log.debug(`🔍 [HELIA_SERVICE] Getting file with CID: ${parsedCid.toString()}`);
      this.log.debug(`   📊 CID Details:`, {
        version: parsedCid.version,
        code: parsedCid.code,
        multihash: {
          code: parsedCid.multihash.code,
          size: parsedCid.multihash.size
        }
      });

      // Get file chunks and combine them
      const chunks: Uint8Array[] = [];
      let bytesLoaded = 0;

      this.log.debug(`   📥 Starting fs.cat() iteration...`);
      try {
        for await (const chunk of node.fs.cat(parsedCid)) {
          chunks.push(chunk);
          bytesLoaded += chunk.length;
          this.log.debug(`   ✅ Chunk received: ${chunk.length} bytes (total: ${bytesLoaded})`);

          if (onProgress != null) {
            onProgress(bytesLoaded, 0); // Total unknown during streaming
          }
        }
      } catch (catError) {
        this.log.error(`   ❌ fs.cat() iteration failed:`, catError);
        this.log.error(`   Error type:`, catError instanceof Error ? catError.constructor.name : typeof catError);
        this.log.error(`   Error message:`, catError instanceof Error ? catError.message : String(catError));
        throw catError;
      }

      this.log.debug(`   ✅ All chunks received, combining...`);
      const result = concatUint8Arrays(chunks);
      this.log.debug(`   ✅ File retrieval complete: ${result.length} bytes`);
      return result;
    } catch (error) {
      throw new Error(`Failed to get file from Helia: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  public async fileExists(node: HeliaNode, cid: string | CID): Promise<boolean> {
    try {
      this.log.debug(`Checking existence of file with CID: ${cid.toString()}`);
      const parsedCid = typeof cid === 'string' ? parseCid(cid) : cid;
      this.log.debug(`Parsed CID: ${parsedCid.toString()}`);
      await node.fs.stat(parsedCid);
      this.log.debug(`File with CID ${parsedCid.toString()} exists.`);
      return true;
    } catch {
      this.log.debug(`File with CID ${cid.toString()} does not exist.`);
      return false;
    }
  }

  public async getFileInfo(node: HeliaNode, cid: string | CID): Promise<{ size: number; type: string }> {
    try {
      const parsedCid = typeof cid === 'string' ? parseCid(cid) : cid;
      const stats = await node.fs.stat(parsedCid);

      return {
        size: Number(stats.size),
        type: 'application/octet-stream' // Default, could be enhanced with type detection
      };
    } catch (error) {
      throw new Error(`Failed to get file info: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ============= BATCH OPERATIONS =============

  public async addMultipleFiles(
    node: HeliaNode,
    files: File[],
    onProgress?: (completed: number, total: number) => void
  ): Promise<AddFileResult[]> {
    const results: AddFileResult[] = [];

    for (let i = 0; i < files.length; i++) {
      const result = await this.addFile(node, files[i]);
      results.push(result);

      if (onProgress != null) {
        onProgress(i + 1, files.length);
      }
    }

    return results;
  }

  public async validateFiles(node: HeliaNode, cids: Array<string | CID>): Promise<boolean[]> {
    const validationPromises = cids.map(async cid => await this.fileExists(node, cid));
    return await Promise.all(validationPromises);
  }

  // ============= PRIVATE HELPER METHODS =============

  private async createPersistentNode(
    libp2p: LibP2pWithServices,
    options: HeliaNodeOptions,
    cacheKey: string
  ): Promise<HeliaNode> {
    try {
      const blockstoreName = options.blockstoreName?.trim() ?? BLOCKSTORE_NAME;
      const datastoreName = options.datastoreName?.trim() ?? DATASTORE_NAME;

      this.log.debug(`   🗄️ Creating persistent Helia node with IDB storage`);
      this.log.debug(`      Blockstore: ${blockstoreName}`);
      this.log.debug(`      Datastore: ${datastoreName}`);

      // Create persistent storage
      const blockstore = new IDBBlockstore(blockstoreName);
      const datastore = new IDBDatastore(datastoreName);

      // Open stores
      await Promise.all([
        blockstore.open(),
        datastore.open()
      ]);
      this.log.debug(`   ✅ IDB stores opened`);

      // Store references for proper cleanup later
      this.storeCache.set(cacheKey, { blockstore, datastore });

      const bitswapConfig = {
        maxInboundStreams: P2P_CONFIG.bitswap.maxInboundStreams,
        maxOutboundStreams: P2P_CONFIG.bitswap.maxOutboundStreams,
        messageSendConcurrency: P2P_CONFIG.bitswap.messageSendConcurrency,
        sendBlocksConcurrency: P2P_CONFIG.bitswap.sendBlocksConcurrency,
        sendBlocksDebounce: P2P_CONFIG.bitswap.sendBlocksDebounce,
        incomingStreamTimeout: P2P_CONFIG.bitswap.incomingStreamTimeout,
        outgoingStreamTimeout: P2P_CONFIG.bitswap.outgoingStreamTimeout,
        sendBlocksTimeout: P2P_CONFIG.bitswap.sendBlocksTimeout,
        receiveBlocksTimeout: P2P_CONFIG.bitswap.receiveBlocksTimeout,
        streamIdleTimeout: P2P_CONFIG.bitswap.streamIdleTimeout
      };

      // Create Helia node with bitswap for P2P block exchange
      const helia = await createHelia({
        libp2p,
        blockstore,
        datastore,
        blockBrokers: [bitswap(bitswapConfig)]
      });
      this.log.debug(`   ✅ Helia instance created`);

      const fs = unixfs(helia);
      this.log.debug(`   ✅ UnixFS layer initialized`);

      return { helia, fs };
    } catch (error) {
      // Clean up store cache if creation failed
      this.storeCache.delete(cacheKey);
      this.log.error(`   ❌ Failed to create persistent Helia node:`, error);
      throw new Error(`Failed to create persistent Helia node: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private async createMemoryNode(libp2p: LibP2pWithServices): Promise<HeliaNode> {
    try {
      this.log.debug('🗄️ Creating in-memory Helia node');

      const bitswapConfig = {
        maxInboundStreams: P2P_CONFIG.bitswap.maxInboundStreams,
        maxOutboundStreams: P2P_CONFIG.bitswap.maxOutboundStreams,
        messageSendConcurrency: P2P_CONFIG.bitswap.messageSendConcurrency,
        sendBlocksConcurrency: P2P_CONFIG.bitswap.sendBlocksConcurrency,
        sendBlocksDebounce: P2P_CONFIG.bitswap.sendBlocksDebounce,
        incomingStreamTimeout: P2P_CONFIG.bitswap.incomingStreamTimeout,
        outgoingStreamTimeout: P2P_CONFIG.bitswap.outgoingStreamTimeout,
        sendBlocksTimeout: P2P_CONFIG.bitswap.sendBlocksTimeout,
        receiveBlocksTimeout: P2P_CONFIG.bitswap.receiveBlocksTimeout,
        streamIdleTimeout: P2P_CONFIG.bitswap.streamIdleTimeout
      };

      const blockstore = new MemoryBlockstore();
      const helia = await createHelia({
        libp2p,
        blockstore,
        blockBrokers: [bitswap(bitswapConfig)]
      });
      const fs = unixfs(helia);

      this.log.debug('✅ Memory Helia node created');
      return { helia, fs };
    } catch (error) {
      throw new Error(`Failed to create memory Helia node: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private generateNodeCacheKey(libp2p: LibP2pWithServices, options: HeliaNodeOptions): string {
    return `${libp2p.peerId.toString()}-${JSON.stringify(options)}`;
  }

  private validateFile(file: File): void {
    if ( !(file instanceof File)) {
      throw new Error('Invalid file provided');
    }

    if (file.size === 0) {
      throw new Error('Cannot add empty file');
    }
  }

  private async *streamToIterator(stream: ReadableStream<Uint8Array>): AsyncIterable<Uint8Array> {
    const reader = stream.getReader();
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) return;
        yield value;
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Pin a CID to prevent garbage collection.
   * Call this after a file is successfully cloned.
   */
  public async pinFile(node: HeliaNode, cid: string | CID): Promise<void> {
    const parsedCid = typeof cid === 'string' ? parseCid(cid) : cid;
    this.log.debug(`Pinning CID: ${parsedCid.toString()}`);

    // pins.add() is an AsyncGenerator — must be consumed to drive the DAG walk
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- consuming generator
    for await (const _block of node.helia.pins.add(parsedCid)) {
      // each yielded CID is a block that was pinned
    }

    this.log.debug(`Pinned CID: ${parsedCid.toString()}`);
  }

  /**
   * Unpin a CID to allow garbage collection.
   */
  public async unpinFile(node: HeliaNode, cid: string | CID): Promise<void> {
    const parsedCid = typeof cid === 'string' ? parseCid(cid) : cid;
    this.log.debug(`Unpinning CID: ${parsedCid.toString()}`);

    // pins.rm() is an AsyncGenerator — must be consumed to drive the DAG walk
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- consuming generator
    for await (const _block of node.helia.pins.rm(parsedCid)) {
      // each yielded CID is a block that was unpinned
    }

    this.log.debug(`Unpinned CID: ${parsedCid.toString()}`);
  }

  /**
   * Check if a CID is pinned.
   */
  public async isPinned(node: HeliaNode, cid: string | CID): Promise<boolean> {
    const parsedCid = typeof cid === 'string' ? parseCid(cid) : cid;
    return await node.helia.pins.isPinned(parsedCid);
  }
}
